import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  NoSymbolIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import api from '../../utils/api';
import { toast } from '../../utils/notification';
import DataTable from '../../components/common/DataTable';

const UsersManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/users/');
      const data = response?.data?.results || response?.data || response || [];
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await api.post(`/api/admin/users/${userId}/set_active_status/`, {
        is_active: !isActive
      });
      await fetchUsers();
      toast.success(`User ${isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      console.error('Error toggling user active status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/admin/users/${userId}/`);
      await fetchUsers();
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(users.map(u => u.id)));
    }
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  const handleBulkActivate = async () => {
    if (!window.confirm(`Activate ${selectedUserIds.size} selected user(s)?`)) return;
    setBulkActionLoading(true);
    try {
      await Promise.all(
        [...selectedUserIds].map(id =>
          api.post(`/api/admin/users/${id}/set_active_status/`, { is_active: true })
        )
      );
      toast.success(`${selectedUserIds.size} user(s) activated`);
      clearSelection();
      await fetchUsers();
    } catch (error) {
      console.error('Bulk activate error:', error);
      toast.error('Some users could not be activated');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (!window.confirm(`Deactivate ${selectedUserIds.size} selected user(s)?`)) return;
    setBulkActionLoading(true);
    try {
      await Promise.all(
        [...selectedUserIds].map(id =>
          api.post(`/api/admin/users/${id}/set_active_status/`, { is_active: false })
        )
      );
      toast.success(`${selectedUserIds.size} user(s) deactivated`);
      clearSelection();
      await fetchUsers();
    } catch (error) {
      console.error('Bulk deactivate error:', error);
      toast.error('Some users could not be deactivated');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedUserIds.size} selected user(s)? This action cannot be undone.`)) return;
    setBulkActionLoading(true);
    try {
      await Promise.all(
        [...selectedUserIds].map(id => api.delete(`/api/admin/users/${id}/`))
      );
      toast.success(`${selectedUserIds.size} user(s) deleted`);
      clearSelection();
      await fetchUsers();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Some users could not be deleted');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Flatten name into the row so DataTable's built-in search/sort can use it.
  const rows = useMemo(
    () =>
      users.map((u) => ({
        ...u,
        full_name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
        role_label: u.is_staff ? 'Admin' : 'User',
      })),
    [users]
  );

  const allSelected = users.length > 0 && selectedUserIds.size === users.length;

  const columns = [
    {
      key: 'select',
      sortable: false,
      headerClassName: 'w-10 px-4',
      className: 'w-10 px-4',
      label: (
        <input
          type="checkbox"
          aria-label="Select all users"
          checked={allSelected}
          onChange={toggleSelectAll}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          aria-label={`Select ${row.full_name || row.email}`}
          checked={selectedUserIds.has(row.id)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectUser(row.id);
          }}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'full_name',
      label: 'Name',
      className: 'text-gray-900 dark:text-white font-medium',
      render: (row) => row.full_name || '—',
    },
    { key: 'email', label: 'Email' },
    { key: 'role_label', label: 'Role' },
    {
      key: 'is_active',
      label: 'Status',
      sortValue: (row) => (row.is_active ? 1 : 0),
      render: (row) => (
        <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
            row.is_active
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}
        >
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'email_verified',
      label: 'Verified',
      sortValue: (row) => (row.email_verified ? 1 : 0),
      render: (row) => (
        <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
            row.email_verified
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
          }`}
        >
          {row.email_verified ? 'Verified' : 'Unverified'}
        </span>
      ),
    },
    {
      key: 'last_login',
      label: 'Last Login',
      sortValue: (row) => (row.last_login ? new Date(row.last_login).getTime() : 0),
      render: (row) => (row.last_login ? new Date(row.last_login).toLocaleString() : 'Never'),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end items-center space-x-3">
          <button
            className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/users/${row.id}/edit`);
            }}
            title="Edit user"
          >
            <PencilSquareIcon className="h-5 w-5" />
          </button>
          <button
            className={`${
              row.is_active ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
            } hover:text-opacity-80 text-sm font-medium`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(row.id, row.is_active);
            }}
            title={row.is_active ? 'Deactivate user' : 'Activate user'}
          >
            {row.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteUser(row.id);
            }}
            title="Delete user"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Users Management</h2>
        <button
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md shadow-sm hover:bg-primary-700"
          onClick={() => navigate('/admin/users/add')}
        >
          <UserPlusIcon className="h-4 w-4 mr-2" /> Add New User
        </button>
      </div>

      {selectedUserIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 px-4 py-3">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
            {selectedUserIds.size} user{selectedUserIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleBulkActivate}
              disabled={bulkActionLoading}
              className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" /> Activate
            </button>
            <button
              onClick={handleBulkDeactivate}
              disabled={bulkActionLoading}
              className="inline-flex items-center gap-1.5 rounded-md bg-yellow-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-yellow-600 disabled:opacity-50"
            >
              <NoSymbolIcon className="h-4 w-4" /> Deactivate
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" /> Delete
            </button>
            <button
              onClick={clearSelection}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-4 w-4" /> Clear
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          defaultSort={{ key: 'full_name', direction: 'asc' }}
          searchPlaceholder="Search users by name, email, or role…"
          searchKeys={['full_name', 'email', 'role_label']}
          emptyMessage="No users found."
          emptyIcon={UsersIcon}
          pageSize={25}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      )}
    </div>
  );
};

export default UsersManagement;
